import { differenceInDays } from 'date-fns'
import { FEATURE_CONTENT_AGE_ALL, FEATURE_CONTENT_AGE_MONTH, FEATURE_CONTENT_AGE_WEEK, FEATURE_CONTENT_AGE_YEAR } from 'shared/constants'
import { PPAddOn } from 'shared/types'
import { camelizeKey } from 'utils/toCamelCase'
import toSnakeCase from 'utils/toSnakeCase'
import { PPProduct } from 'shared'

export const getContentAgeFeature = (date: string | number | Date) => {
  const MAX_FREE_DAYS = 3
  const DAYS_IN_WEEK = 7
  const DAYS_IN_MONTH = 31
  const DAYS_IN_YEAR = 365
  const diffDays = Math.abs(differenceInDays(date, Date.now()))

  // NEW and 3-days are available for all, skip check
  if (diffDays <= MAX_FREE_DAYS) {
    return null
  }

  if (diffDays <= DAYS_IN_WEEK) {
    return FEATURE_CONTENT_AGE_WEEK
  }

  if (diffDays <= DAYS_IN_MONTH) {
    return FEATURE_CONTENT_AGE_MONTH
  }

  if (diffDays <= DAYS_IN_YEAR) {
    return FEATURE_CONTENT_AGE_YEAR
  }

  return FEATURE_CONTENT_AGE_ALL
}

/**
 * Normalises the limit keys on every add-on back to the `UPPER_SNAKE` constants we compare against.
 *
 * The response pipeline camelizes every key, so an add-on's `{ USERS_COUNT: 1 }` arrives as
 * `{ usersCount: 1 }`. This is the same transform `formatProducts` runs over product limits, and
 * it is idempotent - keys that are already upper snake survive untouched.
 */
export function formatAddOns(addons: PPAddOn[]): PPAddOn[] {
  return addons.map(addon => {
    const formatted = toSnakeCase(addon.limits || {})
    const limits = Object.keys(formatted).reduce((map: Record<string, number>, key: string) => {
      map[key.toUpperCase()] = formatted[key]
      return map
    }, {})

    return { ...addon, limits }
  })
}

/**
 * Pairs the quantities the account has purchased with the add-on SKUs they belong to.
 *
 * The response pipeline camelizes every key, which mangles the ids the backend keys `account.addons`
 * by - `addon-images-50` arrives as `addonImages_50`. Ids survive untouched as values on the add-on
 * list, so running the same transform over them recovers the mangled key. The raw id is tried first
 * so this keeps working if the backend ever stops keying the map by id.
 */
export function mapPurchasedAddOns(addons: PPAddOn[], purchased: Record<string, number> = {}): Record<string, number> {
  return addons.reduce((map: Record<string, number>, addon) => {
    const quantity = Number(purchased[addon.id] ?? purchased[camelizeKey(addon.id)])
    if (quantity > 0) {
      map[addon.id] = quantity
    }
    return map
  }, {})
}

export function formatProducts(products: PPProduct[]): PPProduct[] {
  return products.map(p => {
    const featuresFormatted = toSnakeCase(p.features || {})
    const limitsFormatted = toSnakeCase(p.limits || {})

    const features = Object.keys(featuresFormatted).reduce((map: Record<string, boolean>, key: string) => {
      map[key.toUpperCase()] = Boolean(featuresFormatted[key])
      return map
    }, {})

    const limits = Object.keys(limitsFormatted).reduce((map: Record<string, number>, key: string) => {
      map[key.toUpperCase()] = limitsFormatted[key]
      return map
    }, {})

    return { ...p, features, limits }
  })
}
